import React, { useMemo, useState, useContext } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { calculateRowCount } from '../utils/viewUtils';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider, Snackbar, Alert, useTheme } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import HistoryIcon from '@mui/icons-material/History';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './DTCLogView.css';

const PREF_STORAGE_KEY = 'dtc_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
  module: [],
  response: [],
  dtc_type: [],
  status_new: [],
  preexisting: [],
};

const filterConfig = {
  dateFields: [createDateConfig('start_datetime', 'end_datetime', { startParam: 'start_date', endParam: 'end_date', allowEmpty: true })],
  debounceDelay: 100,
};

const DTCLogFilterApp = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 500 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const mapping = { vin: 'vin', mac_id: 'mac_id', module: 'module', response: 'response', dtc_type: 'dtc_type', status_new: 'status_new', preexisting: 'preexisting' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      mac_id: mergeField(debouncedSingleValueFilters.mac_id, debouncedFilters.mac_id),
      module: mergeField(debouncedSingleValueFilters.module, debouncedFilters.module),
      response: mergeField(debouncedSingleValueFilters.response, debouncedFilters.response),
      dtc_type: mergeField(debouncedSingleValueFilters.dtc_type, debouncedFilters.dtc_type),
      status_new: mergeField(debouncedSingleValueFilters.status_new, debouncedFilters.status_new),
      preexisting: mergeField(debouncedSingleValueFilters.preexisting, debouncedFilters.preexisting),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const dtcQuery = useViewQuery({
    queryKey: 'dtc-data',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'api/v2/dtc_logs/query',
  });

  const data = useMemo(() => dtcQuery.data?.results || [], [dtcQuery.data]);
  const rowCount = useMemo(() => calculateRowCount(dtcQuery.data, data, pagination), [dtcQuery.data, data, pagination]);

  const BooleanCell = ({ value }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isTrue = String(value).toLowerCase() === 'true';
    const styles = {
      backgroundColor: isTrue
        ? (isDark ? 'rgba(46, 125, 50, 0.2)' : '#DCFCE7')
        : (isDark ? 'rgba(255, 255, 255, 0.08)' : '#F3F4F6'),
      color: isTrue
        ? (isDark ? '#81c784' : '#166534')
        : (isDark ? '#b0b0b0' : '#374151'),
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 500,
      display: 'inline-block',
      textAlign: 'center',
      minWidth: '40px'
    };
    return <span style={styles}>{isTrue ? 'True' : 'False'}</span>;
  };

  const columns = useMemo(() => [
    { accessorKey: 'vin', header: 'VIN' },
    { accessorKey: 'mac_id', header: 'MAC ID' },
    { accessorKey: 'datetime', header: 'Date', Cell: ({ cell }) => formatDateTime(cell.getValue()) },
    { accessorKey: 'module', header: 'Module' },
    { accessorKey: 'response', header: 'Response', size: 100 },
    { accessorKey: 'status_new', header: 'Status New', Cell: ({ cell }) => <BooleanCell value={cell.getValue()} /> },
    { accessorKey: 'preexisting', header: 'Preexisting', Cell: ({ cell }) => <BooleanCell value={cell.getValue()} /> },
    { accessorKey: 'dtc_type', header: 'DTC Type' },
    { accessorKey: 'status_mask', header: 'Status Mask' },
    { accessorKey: 'failure_type_byte', header: 'FTB' },
    { accessorKey: 'ecu_code', header: 'ECU Code' },
    { accessorKey: 'dtc_description', header: 'DTC Description' },
    { accessorKey: 'ecu_description', header: 'ECU Description' },
  ], [formatDateTime]);

  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="mac_id"
          label="MAC ID"
          value={singleValueFilters.mac_id}
          onChange={(value) => handleSingleFilterChange('mac_id', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={RouterIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('mac_id')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="module"
          label="Module"
          value={singleValueFilters.module}
          onChange={(value) => handleSingleFilterChange('module', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={SettingsIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('module')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="response"
          label="Response"
          value={singleValueFilters.response}
          onChange={(value) => handleSingleFilterChange('response', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={CheckCircleIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('response')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="dtc_type"
          label="DTC Type"
          value={singleValueFilters.dtc_type}
          onChange={(value) => handleSingleFilterChange('dtc_type', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={BugReportIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('dtc_type')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="status_new"
          label="Status New"
          value={singleValueFilters.status_new}
          onChange={(value) => handleSingleFilterChange('status_new', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={CategoryIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('status_new')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="preexisting"
          label="Preexisting"
          value={singleValueFilters.preexisting}
          onChange={(value) => handleSingleFilterChange('preexisting', value)}
          apiBaseUrl="/api/dtc_logs"
          multiple={true}
          icon={HistoryIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('preexisting')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: rowCount,
    isLoading: dtcQuery.isFetching,
    enableRowSelection: false,
    enableRowExpansion: false,
    enableRowActions: false,
    enableCompareFiles: false,
    enableRowVirtualization: true,
    initialPageSize: 500,
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableDefaultPreview: false,
    enableDefaultDownload: false,
    enableDefaultAddToCart: false,
    enableCSVExport: true,
    csvExportFilename: 'dtc-log-export.csv',
    getRowId: (row) => row.id,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    paginationDisplayMode: 'default',
    muiPaginationProps: { 
      color: 'primary', 
      shape: 'rounded', 
      variant: 'outlined', 
      rowsPerPageOptions: [500, 1000],
      showFirstButton: false,
      showLastButton: false
    },
    initialState: {
      columnVisibility: {
        mac_id: false,
        status_mask: false,
        failure_type_byte: false,
        ecu_code: false,
      },
    },
  });


  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="dtc-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters, handleDateRangeChange }}
            timezoneSelector={{ timezone: userTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
          />
        </Box>
        <Box className="dtc-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default DTCLogFilterApp;
